import {
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Button from "../editor/toolbar/Button";
import { useGetTrainingReportQuery } from "../../redux/api/trainingOrder";
import { useGetFleetsQuery } from "../../redux/api/fleet";
import { useGetVesselsQuery } from "../../redux/api/vessel";
import { useState } from "react";
import { Fleet } from "../../common/Fleet";
import { Vessel } from "../../common/Vessel";
import { exportSingleSheetExcel } from "../../util/excelDocument";
import moment from "moment";

const TrainingReportView = (props: {
  defaultStartDate: string|undefined;
  defaultEndDate: string|undefined;
  onClose?: (bol: boolean) => void;
}) => {
  const closeDialog = () => {
    if (!!props.onClose) {
      props.onClose(false);
    }
  };

  const [startDate, setStartDate] = useState<string>(props.defaultStartDate!);
  const [endDate, setEndDate] = useState<string>(props.defaultEndDate!);
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [vessel, setVessel] = useState<Vessel | null>(null);

  const { data: fleets, isLoading: isFleetsLoading } = useGetFleetsQuery(
    {},
    { refetchOnMountOrArgChange: true }
  );
  const { data: vessels, isLoading: isVesselsLoading } = useGetVesselsQuery({});

  const getVesselsList = () => {
    if (!!fleet) {
      return fleet.vessels.filter((v) => !v.disabled);
    } else {
      return vessels ? (vessels as Vessel[]).filter((v) => !v.disabled) : [];
    }
  };

  const { data: report, isLoading: isReportLoading } =
    useGetTrainingReportQuery(
      {
        startDate: startDate || "",
        endDate: endDate || "",
        fleetId: vessel ? undefined : fleet?.id,
        vesselId: vessel?.id,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !startDate || !endDate,
      }
    );

  const exportFile = async () => {
    if (!report) return;

    const start = moment(startDate).format("MM-DD-YYYY");
    const end = moment(endDate).format("MM-DD-YYYY");
    const documentName = vessel?.name
      ? `TrainingReport_${vessel.name}_${start}_to_${end}`
      : fleet?.name
      ? `TrainingReport_${fleet.name}_${start}_to_${end}`
      : `TrainingReport_${start}_to_${end}`;

    await exportSingleSheetExcel(
      {
        data: [
          {
            marinerCount: report.marinerCount,
            courseCount: report.courseCount,
            daysPaidCount: report.daysPaidCount,
          },
        ],
        documentName,
        sheetName: "Report",
      },
      [
        { label: "Mariner Count", name: "marinerCount", width: 20 },
        { label: "Training Course Count", name: "courseCount", width: 25 },
        { label: "Training Days", name: "daysPaidCount", width: 20 },
      ]
    );
  };

  return (
    <div>
      <div className="flex align-items-center justify-content-btwn mb-1">
        <h1 className="mr-2">Mariner Training Report</h1>
        <div className="flex g-1">
          <Button onClick={exportFile}>
            EXPORT TO EXCEL
          </Button>
          {!!props?.onClose && (
            <Button onClick={closeDialog}>
              <CloseIcon />
            </Button>
          )}
        </div>
      </div>
      <div className="filters-wrap mb-2">
        <div className="filters-list flex g-2 align-items-center">
          <label>
            <span>Start Date:</span>
            <input
              type="date"
              value={startDate?.substring(0, 10) || ""}
              onChange={(e) =>
                setStartDate(
                  e.target.value
                    ? new Date(e.target.value).toISOString()
                    : ""
                )
              }
            />
          </label>
          <label>
            <span>End Date:</span>
            <input
              type="date"
              value={endDate?.substring(0, 10) || ""}
              onChange={(e) =>
                setEndDate(
                  e.target.value
                    ? new Date(e.target.value).toISOString()
                    : ""
                )
              }
            />
          </label>
          <label style={{ width: "200px" }}>
            <span>Fleet:</span>
            <Autocomplete
              className="fleet-dropdown"
              options={fleets ? (fleets as Fleet[]) : []}
              value={fleet}
              onChange={(event, value) => {
                setFleet(value);
                setVessel(null);
              }}
              renderInput={(params) => <TextField {...params} />}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              getOptionLabel={(opt) => (opt.name ? opt.name : "")}
              disabled={isFleetsLoading}
            />
          </label>
          <label style={{ width: "200px" }}>
            <span>Vessel:</span>
            <Autocomplete
              className="vessel-dropdown"
              options={getVesselsList()}
              value={vessel}
              onChange={(event, value) => setVessel(value)}
              renderInput={(params) => <TextField {...params} />}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              getOptionLabel={(opt) => (opt.name ? opt.name : "")}
              disabled={isVesselsLoading}
            />
          </label>
        </div>
      </div>

      {isReportLoading && <p>Loading report...</p>}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell className="b-600">Mariner Count</TableCell>
            <TableCell className="b-600">Training Course Count</TableCell>
            <TableCell className="b-600">Training Days</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>{report?.marinerCount ?? 0}</TableCell>
            <TableCell>{report?.courseCount ?? 0}</TableCell>
            <TableCell>{report?.daysPaidCount ?? 0}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

export default TrainingReportView;
